import { Translation } from '@ps/web-shared-components';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import type { TranslationTag } from '@swf/core/types/i18n';
import type { MeetingBasedEventTypeId } from '@swf/generated/types/api/sports';
import { ExpandablePanel, MarketTemplate, Title } from '@swf/sports/components';
import SportsFormattedTimeZone from '@swf/sports/components/SportsFormattedTimeZone/SportsFormattedTimeZone';
import { ScrollableList, Tab } from '@swf/sports/design-system/components';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { EventTypeId } from '@swf/sports/types/eventType';

import {
  TXTWCL_Next_Greyhound_Races,
  TXTWCL_Next_Horse_Races
} from './SportsNextRacesWidget.messages.json';
import styles from './SportsNextRacesWidget.scss';
import RaceInfo from './components/RaceInfo';
import ViewFullRaceCard from './components/ViewFullRaceCard';
import { sportsNextRacesWidgetModel, useSportsNextRacesWidget } from './model';
import type { Template } from './model/types';

const translationMap: Record<MeetingBasedEventTypeId, TranslationTag> = {
  [EventTypeId.HORSE_RACING]: TXTWCL_Next_Horse_Races,
  [EventTypeId.GREYHOUND_RACING]: TXTWCL_Next_Greyhound_Races
};

const SportsNextRacesWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect, widgetConfig } = useSportsNextRacesWidget();
  const templates = useSelector(widgetSelect.templates);
  const races = useSelector(widgetSelect.races);
  const markets = useSelector(widgetSelect.markets);
  const errorCode = useSelector(widgetSelect.errorCode);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const isLoading = useSelector(widgetSelect.isLoading);
  const selectedTemplate = useSelector(widgetSelect.selectedTemplate);
  const marketIds = useSelector(widgetSelect.marketIds);
  const { eventTypeId: urlParamEventTypeId } = useSelector(select.sports.getUrlParams);

  const isCondensedView = widgetConfig.condensed;
  const configuredEventTypeId = widgetConfig.eventTypeId;

  const getRaceWithInfoFromTemplate = useCallback(
    (template: Template) => {
      // TODO: avoid assertion of optional marketId
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { raceId } = markets[template.marketId]!;
      return {
        ...races[raceId as string]
      };
    },
    [races, markets]
  );

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) return null;
  if (errorCode || !templates.length) return null;

  const eventTypeId = configuredEventTypeId ?? urlParamEventTypeId;
  const translation = translationMap[eventTypeId];

  if (!translation || !eventTypeId) return null;

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isNested={isCondensedView}
        title={
          <Title isNested={isCondensedView}>
            <Translation {...translation} />
          </Title>
        }
      >
        <ScrollableList>
          {templates.map((template) => {
            const isActive = selectedTemplate?.marketId === template.marketId;
            if (!isActive && template.hasFinished) return null;
            const { startTime, venue } = getRaceWithInfoFromTemplate(template);

            return (
              <Tab
                key={template.marketId}
                active={isActive}
                onClick={() =>
                  widgetDispatch.setSelected({ ...template, eventTypeId: Number(eventTypeId) })
                }
              >
                <SportsFormattedTimeZone date={startTime} withTime hourCycle="h23" />
                <span className={styles.venue}>{venue}</span>
              </Tab>
            );
          })}
        </ScrollableList>

        {selectedTemplate ? (
          <div className={styles.cardWrapper}>
            <RaceInfo marketId={selectedTemplate.marketId} />
            <MarketTemplate
              templateId={selectedTemplate.templateId}
              marketId={selectedTemplate.marketId}
              templateDisplayConfig={
                isCondensedView
                  ? { RACE_CARD: { maxNumberOfRunners: widgetConfig.maxRunners as number } }
                  : undefined
              }
              templateConfig={selectedTemplate.templateConfig}
            />
            <ViewFullRaceCard marketId={selectedTemplate.marketId} />
          </div>
        ) : null}
      </ExpandablePanel>
    </div>
  );
};

SportsNextRacesWidget.model = sportsNextRacesWidgetModel;

export default withWidgetTestId(SportsNextRacesWidget, 'sports-next-races');
